import {
  createContext,
  type PropsWithChildren,
  useContext,
  useMemo,
  useState,
} from 'react';

import { useAsyncEffect } from '@hivebrite/react-hooks';

import { type BuilderComponent, type RenderComponent } from 'hb-builder/types';
import {
  loadComponentsByType,
  type LoadedComponents,
} from 'hb-builder/utils/componentsLoader';
import { type BUILDER_DOCUMENT_TYPE } from 'hb-react/backoffice/types/builder/builderDocument';
import { APP_TYPE } from 'hb-react/types/appType';

interface ComponentsContextType<T> {
  components: LoadedComponents<T>;
}

type ComponentType<T> = T extends APP_TYPE.BACKOFFICE
  ? BuilderComponent
  : RenderComponent;

const ComponentsContext = createContext<any>(undefined);

export interface ComponentsProviderProps<T extends APP_TYPE> {
  app?: T;
  isPageEvent?: boolean;
  type: BUILDER_DOCUMENT_TYPE;
}

export const ComponentsProvider = <
  T extends APP_TYPE = APP_TYPE.PUBLIC_OFFICE,
>({
  app,
  children,
  type,
  isPageEvent,
}: PropsWithChildren<ComponentsProviderProps<T>>) => {
  const [componentsState, setComponents] =
    useState<LoadedComponents<ComponentType<T>>>();

  useAsyncEffect(async () => {
    const loadedComponents = await loadComponentsByType<ComponentType<T>>({
      type,
      isPageEvent,
      isBackOffice: app === APP_TYPE.BACKOFFICE,
    });

    loadedComponents && setComponents(loadedComponents);
  }, [type]);

  const value: ComponentsContextType<ComponentType<T>> | undefined = useMemo(
    () => (componentsState ? { components: componentsState } : undefined),
    [componentsState],
  );

  if (!value) {
    return null;
  }

  return (
    <ComponentsContext.Provider value={value}>
      {children}
    </ComponentsContext.Provider>
  );
};

const useComponentsContext = <T,>(): ComponentsContextType<T> => {
  const context = useContext<ComponentsContextType<T> | undefined>(
    ComponentsContext,
  );

  if (!context) {
    throw new Error(
      'useComponentsContext must be used within a ComponentsProvider',
    );
  }

  return context;
};

export const useBuilderComponentsContext = () =>
  useComponentsContext<BuilderComponent>();

export const useRenderComponentsContext = () =>
  useComponentsContext<RenderComponent>();
